<template>
  <v-container>
    <template v-if="loading">
      <div class="text-center">
        <v-progress-circular
          size="50"
          color="primary"
          indeterminate
        />
      </div>
    </template>
    <template v-else-if="reservation">
      <v-card
              class='mx-auto ma-6'
              flat
      >
        <v-img
          class='white--text'
          height='200px'
          :src='apartment_image'
          alt='apartment image'
        />
        <v-card-text>
          <v-row >
            <v-col
              cols="auto"
              class="me-auto">
              <v-card-title>{{ $t('order.attributes.code') }}: {{ reservation.order_code }}  </v-card-title>
            </v-col>
            <v-col v-if = "reservation.guest_checkin_state != fastcheckin_completed && reservation.status != checkin_closed" cols="auto">
              <router-link
                :to='{
                  name: "checkinGuests",
                  params: {
                    uuid: reservation.order_uuid,
                    reservation_id: reservation.id
                  }
                }'
                style="width: 150px"
                class="no-underline"  >
                <v-btn block color='primary' >
                  {{ $t('order.checkin.show') }}
                </v-btn>
              </router-link>
            </v-col>
          </v-row>
          <v-divider class="mt-2 mb-2"></v-divider>
          <v-row>
            <v-icon large class='ml-2'>mdi-book-outline</v-icon>
            <v-col>
              {{ $t('order.attributes.ota') }}<br>
              <strong>{{ reservation.order_ota || 'nd' }}</strong>
            </v-col>
            <v-icon large>mdi-home-city-outline</v-icon>
            <v-col>
              {{ $t('reservation.attributes.apartment') }}<br>
              <strong v-html="apartment_link"></strong>
            </v-col>
          </v-row>
          <v-row>
            <v-icon large class='ml-2'>mdi-home-import-outline</v-icon>
            <v-col>
              {{ $t('reservation.attributes.checkin_date') }}<br>
              <strong>{{ formattedDate(reservation.checkin_date) }}</strong>
            </v-col>
            <v-icon large>mdi-home-export-outline</v-icon>
            <v-col>
              {{ $t('reservation.attributes.checkout_date') }}<br>
              <strong>{{ formattedDate(reservation.checkout_date) }}</strong>
            </v-col>
          </v-row>
          <v-row>
            <v-icon large class='ml-2'>mdi-weather-night</v-icon>
            <v-col>
              {{ $t('reservation.attributes.nights') }}<br>
              <strong>{{ bookedNights }}</strong>
            </v-col>
            <v-icon large>mdi-account-multiple</v-icon>
            <v-col v-bind:class="{ 'incomplete': reservation.guests.length != reservation.booked_guests}">
              {{ $t('reservation.attributes.guests') }}<br>
              <strong>{{ reservation.guests.length }} / {{ reservation.booked_guests }}</strong>
            </v-col>
          </v-row>
          <div v-if="reservation.status == checkout_closed || reservation.status == checkin_closed">
            <v-divider v-if="reservation.folders.length > 0"></v-divider>
            <v-row v-if="reservation.folders.length > 0">
              <v-col>
                <v-list>
                  <v-list-group
                    v-for='folder in reservation.folders' :key='folder.id'
                    :value=false
                    prepend-icon="mdi-folder"
                  >
                    <template v-slot:activator>
                      <v-list-item-title class="text-start">{{ folder.name }}</v-list-item-title>
                    </template>
                    <v-list-item v-for='manual in folder.manuals' :key='manual.id'>
                      <a :href='manual.url' target="_blank">{{ manual.file_name }}</a>
                    </v-list-item>
                  </v-list-group>
                </v-list>
              </v-col>
            </v-row>
            <v-divider v-if="reservation.doors_instruction.length > 0"></v-divider>
            <DropdownUrl v-if="reservation.doors_instruction.length > 0"
                         :title="$t('order.checkin.door_instruction')"
                         :elements="reservation.doors_instruction" />
            <v-divider v-if="reservation.contracts.length > 0"></v-divider>
            <DropdownUrl v-if="reservation.contracts.length > 0"
                         :title="$t('order.checkin.contracts')"
                         :elements="reservation.contracts" />
            <v-divider v-if="reservation.invoices.length > 0"></v-divider>
            <DropdownUrl v-if="reservation.invoices.length > 0"
                         :title="$t('order.checkin.invoices')"
                         :elements="reservation.invoices"
                         :add_locale = true
            />
          </div>
          <v-divider v-if="reservation.guests.length > 0"></v-divider>
          <v-row>
            <v-col
              v-for="guest in reservation.guests"
              :key="guest.id"
            >
              <v-col>
                <GuestCard :guest="guest"/>
              </v-col>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { utilityMixin } from "../../mixins/utility_mixin";
import { reservationMixin } from "../../mixins/reservation_mixin";
import { apartmentMixin } from '../../mixins/apartment_mixin';
import GuestCard from '../guests/guest_card';
import DropdownUrl from '../layout/dropdown_url'

export default {
  name: "ReservationDetails",
  components: {
    GuestCard,
    DropdownUrl
  },
  mixins: [
    utilityMixin,
    apartmentMixin,
    reservationMixin,
  ],
  data() {
    return {
      reservation: null,
      checkin_closed: 'CHECKIN_CLOSED',
      checkout_closed: 'CHECKOUT_CLOSED',
      fastcheckin_completed: 'completed'
    }
  },
  computed: {
    ...mapState('layout', ['loading']),
  },
  created() {
    this.$store.dispatch(`order/reservationDetail`, {
      reservationId: this.$route.params.id
    }).then(result => {
      this.reservation = result
    }).catch(error => {
      this.serverSideErrors = error.body ? error.body.errors : {}
      if (error.status == 404){
        this.$router.push({name: 'dashboard'})
      }
      if (!error.status) {
        this.$store.dispatch('layout/replaceAlert', {
          type: 'error',
          resource: 'recipient',
          fields: this.serverSideErrors,
        })
      }
    })
  }
}
</script>

<style lang='scss' scoped>
  .no-underline {
    text-decoration: none;
  }

  .guests--incomplete {
    color: red;
  }
</style>
